import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and hospital context
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Super Admin hospital context
    const hospitalId = localStorage.getItem('selectedHospitalId');
    if (hospitalId) {
      config.headers['X-Hospital-Id'] = hospitalId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  googleSession: (session_id) => api.post('/auth/google/session', { session_id }),
  resetStaffAccess: (staffId) => api.post(`/staff/${staffId}/reset-access`),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Hospitals API
export const hospitalsAPI = {
  list: () => api.get('/hospitals/'),
  get: (id) => api.get(`/hospitals/${id}`),
  create: (data) => api.post('/hospitals/', data),
  update: (id, data) => api.put(`/hospitals/${id}`, data),
  delete: (id) => api.delete(`/hospitals/${id}`),
  // Branding APIs
  getBranding: (id) => api.get(`/hospitals/${id}/branding`),
  updateBranding: (id, data) => api.put(`/hospitals/${id}/branding`, data),
  updateInfo: (id, data) => api.patch(`/hospitals/${id}/info`, data),
};

// Users API
export const usersAPI = {
  list: () => api.get('/users/'),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Roles API
export const rolesAPI = {
  list: () => api.get('/roles/'),
  get: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles/', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  landingOptions: () => api.get('/roles/landing-options'),
};

// Permissions API
export const permissionsAPI = {
  list: () => api.get('/permissions'),
};

// Subscriptions API
export const subscriptionsAPI = {
  listPlans: () => api.get('/subscriptions/plans'),
  getPlan: (id) => api.get(`/subscriptions/plans/${id}`),
  createPlan: (data) => api.post('/subscriptions/plans', data),
  updatePlan: (id, data) => api.put(`/subscriptions/plans/${id}`, data),
};

// Audit Logs API
export const auditAPI = {
  list: (params = {}) => api.get('/audit/', { params }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  commandCenter: (params = {}) => api.get('/dashboard/command-center', { params }),
  doctorDetail: (doctorId, params = {}) => api.get(`/dashboard/doctor/${doctorId}`, { params }),
};

// Doctor Dashboard API
export const doctorDashboardAPI = {
  me: () => api.get('/doctor-dashboard/me'),
  all: () => api.get('/doctor-dashboard/all'),
  get: (doctorId) => api.get(`/doctor-dashboard/${doctorId}`),
  // Item 6 — doctor self-managed schedule + attendance seed
  updateMySchedule: (schedule, force = false) => api.put('/doctors/me/schedule', { schedule, force }),
  checkScheduleConflicts: (schedule) => api.post('/doctors/me/schedule/conflicts', { schedule }),
  seedMyAttendance: () => api.post('/doctors/me/seed-attendance'),
};

// Patient Dashboard API
export const patientDashboardAPI = {
  overview: () => api.get('/patient-dashboard/overview'),
  detail: (patientId) => api.get(`/patient-dashboard/detail/${patientId}`),
  search: (q) => api.get('/patient-dashboard/search', { params: { q } }),
  seedDemoData: () => api.post('/patient-enhancements/seed-demo-data'),
  riskScore: (patientId) => api.get(`/patient-enhancements/risk-score/${patientId}`),
  bodyMap: (patientId) => api.get(`/patient-enhancements/body-map/${patientId}`),
};

// Features API
export const featuresAPI = {
  getAvailable: () => api.get('/features'),
};

// Domain Resolution API (public, no auth)
export const domainAPI = {
  resolve: (slug) => api.get('/domain/resolve', { params: { slug } }),
  listHospitals: () => api.get('/domain/hospitals'),
};

// Departments API
export const departmentsAPI = {
  list: () => api.get('/departments/'),
  create: (data) => api.post('/departments/', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Staff API
export const staffAPI = {
  list: () => api.get('/staff/'),
  getMe: () => api.get('/staff/me'),
  get: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff/', data),
  onboard: (data) => api.post('/staff/onboard', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  seedTestData: () => api.post('/seed-test-staff'),
  bulkUploadTemplate: () => api.get('/staff/bulk-upload/template', { responseType: 'blob' }),
  bulkUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/staff/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Account status (security snapshot) + unlock — May 15, 2026
  accountStatus: (staffId) => api.get(`/staff/${staffId}/account-status`),
  unlockAccount: (staffId) => api.post(`/staff/${staffId}/unlock-account`),
  // Role / Activity History — May 15, 2026 v31
  history: (staffId) => api.get(`/staff/${staffId}/history`),
  // v37 — self profile update (any user can update own KYC/personal fields)
  updateMyProfile: (data) => api.put('/staff/me', data),
  // v38 — Profile photo upload (self-service)
  uploadMyPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/staff/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteMyPhoto: () => api.delete('/staff/me/photo'),
};

// v37 — Holidays / Celebrations / KYC
export const v37API = {
  // Holidays
  listHolidays: (year) => api.get('/v37/holidays', { params: year ? { year } : {} }),
  createHoliday: (data) => api.post('/v37/holidays', data),
  updateHoliday: (id, data) => api.put(`/v37/holidays/${id}`, data),
  deleteHoliday: (id) => api.delete(`/v37/holidays/${id}`),
  bulkHolidays: (rows) => api.post('/v37/holidays/bulk', { holidays: rows }),
  // Celebrations
  celebrationsToday: () => api.get('/v37/celebrations/today'),
  // Celebrations v37 Batch 2 — auto/manual greetings
  getCelebrationSettings: () => api.get('/v37/celebrations/settings'),
  updateCelebrationSettings: (data) => api.put('/v37/celebrations/settings', data),
  previewCelebrationCard: (data) => api.post('/v37/celebrations/preview', data),
  sendCelebrationsNow: () => api.post('/v37/celebrations/send-now'),
  listCelebrationsToday: () => api.get('/v37/celebrations/list-today'),
  // KYC
  kycStatus: () => api.get('/v37/kyc/status'),
  kycCompliance: (only_incomplete = true) => api.get('/v37/kyc/compliance', { params: { only_incomplete } }),
  sendKycReminders: (staff_ids) => api.post('/v37/kyc/send-reminders', { staff_ids }),
  // Shifts (edit/delete + bulk-pattern)
  updateShift: (id, data) => api.put(`/v37/shifts/assignments/${id}`, data),
  deleteShift: (id) => api.delete(`/v37/shifts/assignments/${id}`),
  bulkShiftPattern: (data) => api.post('/v37/shifts/assignments/bulk-pattern', data),
};

// Attendance API
export const attendanceAPI = {
  getToday: () => api.get('/attendance/today'),
  markIn: (data = {}) => api.post('/attendance/mark-in', data),
  markOut: (data = {}) => api.post('/attendance/mark-out', data),
  getReport: (params = {}) => api.get('/attendance/report', { params }),
  getDashboard: () => api.get('/attendance/dashboard'),
  getUserMonthly: (userId, params = {}) => api.get(`/attendance/user/${userId}/monthly`, { params }),
  adminMark: (data) => api.post('/attendance/admin-mark', data),
  staffSearch: (q = '') => api.get('/attendance/staff-search', { params: { q } }),
  // v38 — Proxy attendance (Front Desk / IT Admin / Hospital Admin only)
  proxyMarkIn: (targetUserId) => api.post(`/attendance/proxy-mark-in/${targetUserId}`),
  proxyMarkOut: (targetUserId) => api.post(`/attendance/proxy-mark-out/${targetUserId}`),
  todayStatus: (targetUserId) => api.get(`/attendance/today-status/${targetUserId}`),
};

// Leave API
export const leaveAPI = {
  getTypes: () => api.get('/leave/types'),
  createType: (data) => api.post('/leave/types', data),
  getBalance: () => api.get('/leave/balance'),
  getApplications: (params = {}) => api.get('/leave/applications', { params }),
  apply: (data) => api.post('/leave/apply', data),
  approve: (id, data) => api.put(`/leave/applications/${id}/approve`, data),
  getCalendar: (params = {}) => api.get('/leave/calendar', { params }),
};

// Notice API
export const noticeAPI = {
  list: () => api.get('/notices/'),
  get: (id) => api.get(`/notices/${id}`),
  create: (data) => api.post('/notices/', data),
  update: (id, data) => api.put(`/notices/${id}`, data),
  acknowledge: (id) => api.post(`/notices/${id}/acknowledge`),
  dashboard: (params = {}) => api.get('/notices/dashboard/stats', { params }),
};

// Doctor Config API
export const doctorAPI = {
  list: () => api.get('/doctors/'),
  getMe: () => api.get('/doctors/me'),
  create: (data) => api.post('/doctors/', data),
  update: (id, data) => api.put(`/doctors/${id}`, data),
};

// Security API
export const securityAPI = {
  getSettings: () => api.get('/security/settings'),
  updateSettings: (data) => api.put('/security/settings', data),
  getDeviceLogs: (params = {}) => api.get('/security/device-logs', { params }),
};

// Patients API
export const patientsAPI = {
  list: (params = {}) => api.get('/patients/', { params }),
  count: () => api.get('/patients/count'),
  get: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients/', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  toggleVip: (id, data) => api.put(`/patients/${id}/vip`, data),
  vipDashboard: () => api.get('/patients/vip/dashboard'),
  getHistory: (id) => api.get(`/patients/${id}/history`),
  getSettings: () => api.get('/patients/config/hospital-settings'),
  updateSettings: (data) => api.put('/patients/config/hospital-settings', data),
  getDoctors: () => api.get('/patients/config/doctors'),
};

// Visits API
export const visitsAPI = {
  list: (params = {}) => api.get('/visits/', { params }),
  get: (id) => api.get(`/visits/${id}`),
  create: (data) => api.post('/visits/', data),
  update: (id, data) => api.put(`/visits/${id}`, data),
};

// Vitals API
export const vitalsAPI = {
  list: (params) => api.get('/vitals/', { params }),
  create: (data) => api.post('/vitals/', data),
};

// Diagnoses API
export const diagnosesAPI = {
  list: (params = {}) => api.get('/diagnoses/', { params }),
  create: (data) => api.post('/diagnoses/', data),
};

// Legacy in-visit Prescriptions API (used by PatientDetailPage's EMR Rx flow)
export const prescriptionsAPI = {
  list: (params = {}) => api.get('/prescriptions/', { params }),
  create: (data) => api.post('/prescriptions/', data),
};

// Prescriptions Rx (C2 — standalone Rx → Pharmacy hand-off, new module)
// Targets the new `/api/rx` module for cross-pharmacy fulfilment.
export const rxAPI = {
  list: (params = {}) => api.get('/rx/', { params }),
  get: (id) => api.get(`/rx/${id}`),
  create: (data) => api.post('/rx/', data),
  update: (id, data) => api.put(`/rx/${id}`, data),
  handToPharmacy: (id) => api.post(`/rx/${id}/dispense`),
  markDispensed: (id, dispenseId) => api.post(`/rx/${id}/mark-dispensed?dispense_id=${dispenseId}`),
};

// Clinical Notes API
export const clinicalNotesAPI = {
  list: (params = {}) => api.get('/clinical-notes/', { params }),
  create: (data) => api.post('/clinical-notes/', data),
  update: (id, data) => api.put(`/clinical-notes/${id}`, data),
};

// Lab Reports API
export const labReportsAPI = {
  list: (params = {}) => api.get('/lab-reports/', { params }),
  create: (data) => api.post('/lab-reports/', data),
  update: (id, data) => api.put(`/lab-reports/${id}`, data),
};

// Files API
export const filesAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (fileId) => api.get(`/files/download/${fileId}`, { responseType: 'blob' }),
};

// Doctor Schedules API
export const schedulesAPI = {
  list: (params = {}) => api.get('/doctor-schedules/', { params }),
  create: (data) => api.post('/doctor-schedules/', data),
  update: (id, data) => api.put(`/doctor-schedules/${id}`, data),
  delete: (id) => api.delete(`/doctor-schedules/${id}`),
};

// Appointments API
export const appointmentsAPI = {
  list: (params = {}) => api.get('/appointments/', { params }),
  count: (params = {}) => api.get('/appointments/count', { params }),
  get: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments/', data),
  updateStatus: (id, data) => api.put(`/appointments/${id}/status`, data),
  availableSlots: (doctorId, date) => api.get('/appointments/available-slots', { params: { doctor_id: doctorId, date } }),
};

// Queue API
export const queueAPI = {
  today: (params = {}) => api.get('/queue/today', { params }),
  doctorsToday: () => api.get('/queue/doctors-today'),
};

// Invoices API
export const invoicesAPI = {
  list: (params = {}) => api.get('/invoices/', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices/', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
};

// Wards API
export const wardsAPI = {
  list: () => api.get('/wards/'),
  get: (id) => api.get(`/wards/${id}`),
  create: (data) => api.post('/wards/', data),
  update: (id, data) => api.put(`/wards/${id}`, data),
  delete: (id) => api.delete(`/wards/${id}`),
};

// Beds API
export const bedsAPI = {
  list: (params = {}) => api.get('/beds/', { params }),
  dashboard: () => api.get('/beds/dashboard'),
  updateStatus: (id, data) => api.put(`/beds/${id}/status`, data),
  tempReserve: (id, data) => api.post(`/beds/${id}/temp-reserve`, data),
  releaseReservation: (id) => api.post(`/beds/${id}/release-reservation`),
};

// IPD Admissions API
export const admissionsAPI = {
  list: (params = {}) => api.get('/admissions/', { params }),
  get: (id) => api.get(`/admissions/${id}`),
  create: (data) => api.post('/admissions/', data),
  discharge: (id, data) => api.put(`/admissions/${id}/discharge`, data),
  transfer: (id, data) => api.put(`/admissions/${id}/transfer`, data),
};

// Billing Config API
export const billingAPI = {
  getConfig: () => api.get('/billing/config'),
  updateConfig: (data) => api.put('/billing/config', data),
  createInvoice: (data) => api.post('/billing/create-invoice', data),
  getLeaveTypes: () => api.get('/billing/leave-types'),
  createLeaveType: (data) => api.post('/billing/leave-types', data),
  updateLeaveType: (id, data) => api.put(`/billing/leave-types/${id}`, data),
  deleteLeaveType: (id) => api.delete(`/billing/leave-types/${id}`),
};

// Procedures API
export const proceduresAPI = {
  list: () => api.get('/procedures/'),
  create: (data) => api.post('/procedures/', data),
  update: (id, data) => api.put(`/procedures/${id}`, data),
  delete: (id) => api.delete(`/procedures/${id}`),
};

// Payments API
export const paymentsAPI = {
  getForInvoice: (invoiceId) => api.get(`/payments/${invoiceId}`),
  addPayment: (invoiceId, data) => api.post(`/payments/${invoiceId}`, data),
  list: (params = {}) => api.get('/payments/', { params }),
  get: (id) => api.get(`/payments/detail/${id}`),
  create: (data) => api.post('/payments/', data),
  getForPatient: (patientId) => api.get(`/payments/patient/${patientId}`),
  history: (params = {}) => api.get('/payments/history/search', { params }),
  staffList: () => api.get('/payments/history/staff'),
};

export const servicesAPI = {
  list: (params = {}) => api.get('/services/', { params }),
  create: (data) => api.post('/services/', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Payroll API
export const payrollAPI = {
  doctorPayouts: (params = {}) => api.get('/payroll/doctor-payouts', { params }),
  staffPayroll: (params = {}) => api.get('/payroll/staff-payroll', { params }),
};

// Revenue API
export const revenueAPI = {
  summary: (params = {}) => api.get('/revenue/summary', { params }),
};

// Vendors API
export const vendorsAPI = {
  list: () => api.get('/vendors/'),
  get: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors/', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Expense Categories API
export const expenseCategoriesAPI = {
  list: () => api.get('/expense-categories/'),
  create: (data) => api.post('/expense-categories/', data),
  delete: (id) => api.delete(`/expense-categories/${id}`),
};

// Expenses API
export const expensesAPI = {
  list: (params = {}) => api.get('/expenses/', { params }),
  dashboard: (params = {}) => api.get('/expenses/dashboard', { params }),
  create: (data) => api.post('/expenses/', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// P&L API
export const pnlAPI = {
  daily: (params = {}) => api.get('/pnl/daily', { params }),
  monthly: (params = {}) => api.get('/pnl/monthly', { params }),
  exportExcel: (params = {}) => api.get('/pnl/export/excel', { params, responseType: 'blob' }),
  exportPDF: (params = {}) => api.get('/pnl/export/pdf', { params, responseType: 'blob' }),
};

// Finance Config API
export const financeConfigAPI = {
  get: () => api.get('/finance-config/'),
  update: (data) => api.put('/finance-config/', data),
  toggleMonthLock: (data) => api.post('/finance-config/month-lock', data),
};

// Service Catalog API
export const serviceCatalogAPI = {
  list: (params = {}) => api.get('/services/', { params }),
  create: (data) => api.post('/services/', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  categories: (params = {}) => api.get('/services/categories', { params }),
};

// Allergies API
export const allergiesAPI = {
  getByPatient: (patientId) => api.get(`/allergies/patient/${patientId}`),
  create: (data) => api.post('/allergies/', data),
  update: (id, data) => api.put(`/allergies/${id}`, data),
  delete: (id) => api.delete(`/allergies/${id}`),
  checkDrug: (patientId, drugName) => api.get(`/allergies/check/${patientId}/${encodeURIComponent(drugName)}`),
};

// Discharge Summary API
export const dischargeAPI = {
  getByPatient: (patientId) => api.get(`/discharge/patient/${patientId}`),
  get: (id) => api.get(`/discharge/${id}`),
  create: (data) => api.post('/discharge/', data),
  update: (id, data) => api.put(`/discharge/${id}`, data),
  finalize: (id) => api.put(`/discharge/${id}/finalize`),
};

// Bed Status API
export const bedStatusAPI = {
  getBoard: (params = {}) => api.get('/bed-status/', { params }),
  updateBed: (wardId, bedNumber, data) => api.put(`/bed-status/bed/${wardId}/${bedNumber}`, data),
};

// Refund API
export const refundAPI = {
  list: (params = {}) => api.get('/refunds/', { params }),
  create: (data) => api.post('/refunds/', data),
  approve: (id, data) => api.put(`/refunds/${id}/approve`, data),
  process: (id) => api.put(`/refunds/${id}/process`),
  reject: (id, data) => api.put(`/refunds/${id}/reject`, data),
};

// Audit Logs API
export const auditLogAPI = {
  list: (params = {}) => api.get('/audit-logs/', { params }),
  summary: (params = {}) => api.get('/audit-logs/summary', { params }),
};

// ==================== BATCH 2 (H3-H14) ====================

// H3: Consultation Types
export const consultationTypesAPI = {
  list: (params = {}) => api.get('/consultation-types/', { params }),
  create: (data) => api.post('/consultation-types/', data),
  update: (id, data) => api.put(`/consultation-types/${id}`, data),
  delete: (id) => api.delete(`/consultation-types/${id}`),
};

// H4: Doctor Availability
export const doctorAvailabilityAPI = {
  get: (doctorId, params = {}) => api.get(`/doctor-availability/doctor/${doctorId}`, { params }),
  set: (doctorId, data) => api.post(`/doctor-availability/doctor/${doctorId}`, data),
  blockDate: (doctorId, data) => api.post(`/doctor-availability/doctor/${doctorId}/block-date`, data),
  unblockDate: (doctorId, blockId) => api.delete(`/doctor-availability/doctor/${doctorId}/block-date/${blockId}`),
  getSlots: (doctorId, date) => api.get(`/doctor-availability/doctor/${doctorId}/slots`, { params: { date } }),
  listAll: (hospitalId) => api.get(`/doctor-availability/hospital/${hospitalId}`),
};

// H7: Vitals (Enhanced)
export const vitalsEnhancedAPI = {
  history: (patientId, limit = 50) => api.get(`/vitals-enhanced/patient/${patientId}/history`, { params: { limit } }),
  latest: (patientId) => api.get(`/vitals-enhanced/patient/${patientId}/latest`),
  record: (data) => api.post('/vitals-enhanced/', data),
  alerts: (params = {}) => api.get('/vitals-enhanced/alerts', { params }),
};

// H8: Recurring Appointments
export const recurringAppointmentsAPI = {
  list: (params = {}) => api.get('/recurring-appointments/', { params }),
  create: (data) => api.post('/recurring-appointments/', data),
  cancel: (id) => api.delete(`/recurring-appointments/${id}`),
};

// H9: Appointment Reschedule
export const rescheduleAPI = {
  reschedule: (appointmentId, data) => api.post(`/appointment-reschedule/${appointmentId}`, data),
  undo: (appointmentId) => api.post(`/appointment-reschedule/${appointmentId}/undo`),
  history: (appointmentId) => api.get(`/appointment-reschedule/history/${appointmentId}`),
};

// H10: Reminders
export const remindersAPI = {
  list: (params = {}) => api.get('/reminders/', { params }),
  create: (data) => api.post('/reminders/', data),
  templates: (params = {}) => api.get('/reminders/templates', { params }),
  generate: (data) => api.post('/reminders/generate', data),
};

// H12: Purchase Orders
export const purchaseOrdersAPI = {
  list: (params = {}) => api.get('/purchase-orders/', { params }),
  create: (data) => api.post('/purchase-orders/', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  approve: (id, data) => api.put(`/purchase-orders/${id}/approve`, data),
  send: (id) => api.put(`/purchase-orders/${id}/send`),
  receive: (id, data) => api.put(`/purchase-orders/${id}/receive`, data),
  summary: (params = {}) => api.get('/purchase-orders/summary', { params }),
};

// H13: Shifts
export const shiftsAPI = {
  listTypes: (params = {}) => api.get('/shifts/types', { params }),
  createType: (data) => api.post('/shifts/types', data),
  listAssignments: (params = {}) => api.get('/shifts/assignments', { params }),
  createAssignment: (data) => api.post('/shifts/assignments', data),
  bulkAssign: (data) => api.post('/shifts/assignments/bulk', data),
  swapRequest: (data) => api.post('/shifts/swap-request', data),
};

// H14: Payroll Enhanced
export const payrollEnhancedAPI = {
  getStructure: (staffId) => api.get(`/payroll-enhanced/structure/${staffId}`),
  setStructure: (data) => api.post('/payroll-enhanced/structure', data),
  listStructures: (params = {}) => api.get('/payroll-enhanced/structures', { params }),
  generatePayslip: (data) => api.post('/payroll-enhanced/generate-payslip', data),
  listPayslips: (params = {}) => api.get('/payroll-enhanced/payslips', { params }),
};

// Nurse Module (Phase B)
export const nurseAPI = {
  dashboard: (allWards = false) => api.get('/nurse/dashboard', { params: { all_wards: allWards } }),
  myWard: () => api.get('/nurse/my-ward'),
  assignWard: (userId, wardId) => api.put(`/nurse/${userId}/assign-ward`, { ward_id: wardId }),
  recordVitals: (data) => api.post('/nurse/vitals', data),
  vitalsTrend: (patientId) => api.get(`/nurse/vitals/${patientId}`),
  medications: (allWards = false) => api.get('/nurse/medications', { params: { all_wards: allWards } }),
  administer: (data) => api.post('/nurse/medications/administer', data),
  medicationLog: (patientId) => api.get(`/nurse/medications/log/${patientId}`),
  // Phase C — Shift Handover
  getShiftHandover: (params = {}) => api.get('/nurse/shift-handover', { params }),
  saveShiftHandover: (data) => api.post('/nurse/shift-handover', data),
  // Phase C — Admission / Discharge Checklists
  getAdmissionChecklist: (admissionId) => api.get(`/nurse/admission-checklist/${admissionId}`),
  saveAdmissionChecklist: (admissionId, data) => api.post(`/nurse/admission-checklist/${admissionId}`, data),
  getDischargeChecklist: (admissionId) => api.get(`/nurse/discharge-checklist/${admissionId}`),
  saveDischargeChecklist: (admissionId, data) => api.post(`/nurse/discharge-checklist/${admissionId}`, data),
  // Phase C — Emergency
  triggerEmergency: (data) => api.post('/nurse/emergency', data),
  listEmergencies: (params = {}) => api.get('/nurse/emergency', { params }),
  resolveEmergency: (eventId, notes) => api.put(`/nurse/emergency/${eventId}/resolve`, { notes }),
  // Phase D — admin helper
  listNurses: () => api.get('/nurse/list'),
};

export const doctorOrdersAPI = {
  list: (params = {}) => api.get('/doctor-orders/', { params }),
  create: (data) => api.post('/doctor-orders/', data),
  accept: (orderId) => api.put(`/doctor-orders/${orderId}/accept`),
  complete: (orderId, notes) => api.put(`/doctor-orders/${orderId}/complete`, { notes }),
  escalate: (orderId, reason) => api.put(`/doctor-orders/${orderId}/escalate`, { reason }),
};

// Pharmacy (C1 — drug master, batch inventory, dispensing)
export const pharmacyAPI = {
  // Drug master
  listDrugs: (params = {}) => api.get('/pharmacy/drugs', { params }),
  createDrug: (data) => api.post('/pharmacy/drugs', data),
  updateDrug: (id, data) => api.put(`/pharmacy/drugs/${id}`, data),
  deleteDrug: (id) => api.delete(`/pharmacy/drugs/${id}`),
  // Batches
  listBatches: (params = {}) => api.get('/pharmacy/batches', { params }),
  createBatch: (data) => api.post('/pharmacy/batches', data),
  deleteBatch: (id) => api.delete(`/pharmacy/batches/${id}`),
  // Dispenses
  listDispenses: (params = {}) => api.get('/pharmacy/dispenses', { params }),
  getDispense: (id) => api.get(`/pharmacy/dispenses/${id}`),
  createDispense: (data) => api.post('/pharmacy/dispenses', data),
  // Stock summary
  summary: () => api.get('/pharmacy/summary'),
};

// Lab (C3 — tests catalog + orders + results)
export const labAPI = {
  listTests: () => api.get('/lab/tests'),
  createTest: (data) => api.post('/lab/tests', data),
  deleteTest: (id) => api.delete(`/lab/tests/${id}`),
  listOrders: (params = {}) => api.get('/lab/orders', { params }),
  createOrder: (data) => api.post('/lab/orders', data),
  enterResults: (orderId, results) => api.put(`/lab/orders/${orderId}/results`, { results }),
};

// Generic CRUD for C4-C9
const crudClient = (prefix) => ({
  list: () => api.get(`/${prefix}/`),
  get: (id) => api.get(`/${prefix}/${id}`),
  create: (data) => api.post(`/${prefix}/`, data),
  update: (id, data) => api.put(`/${prefix}/${id}`, data),
  delete: (id) => api.delete(`/${prefix}/${id}`),
});

export const imagingAPI = crudClient('imaging');
export const insuranceAPI = crudClient('insurance');
export const bloodBankAPI = crudClient('bloodbank');
export const storeAPI = crudClient('store');
export const mortuaryAPI = crudClient('mortuary');
export const canteenAPI = crudClient('canteen');

// v37b2 — IPD Referrals (OPD → IPD)
export const ipdReferralsAPI = {
  list: (params = {}) => api.get('/ipd-referrals/', { params }),
  get: (id) => api.get(`/ipd-referrals/${id}`),
  create: (data) => api.post('/ipd-referrals/', data),
  cancel: (id, reason) => api.put(`/ipd-referrals/${id}/cancel`, { cancelled_reason: reason }),
  latestPendingForPatient: (patientId) => api.get(`/ipd-referrals/patient/${patientId}/latest-pending`),
};


// v37b2 — Hospital 360° (Finance + Statistics)
export const finance360API = {
  dashboard: (params = {}) => api.get('/finance/dashboard', { params }),
  trend: (params = {}) => api.get('/finance/dashboard/trend', { params }),
  drilldown: (params = {}) => api.get('/finance/dashboard/drilldown', { params }),
  exportCsv: (params = {}) => api.get('/finance/dashboard/export.csv', { params, responseType: 'blob' }),
  filterOptions: () => api.get('/finance/filter-options'),
  comparison: (params = {}) => api.get('/stats360/comparison', { params }),
};

// v37b2 — Universal Feedback (Code Blue)
export const feedbackAPI = {
  create: (data) => api.post('/feedback/', data),
  list: (params = {}) => api.get('/feedback/', { params }),
  summary: (params = {}) => api.get('/feedback/summary', { params }),
  staffOptions: () => api.get('/feedback/staff-options'),
};


export default api;
